import React, { useState, useEffect } from "react"
import { AuthFlowStep, AuthFlowFormField, AuthFlowHttpRequestDetail } from "./gql"
import { StepState, StepStatus, getStatusDisplayClass } from "./step_state_reducer"
import AuthFlowStepForm from "./auth_flow_step_form"

// --- Utility functions ---

function formatStepType(stepType: string): string {
  return stepType
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

function formatInputMethod(inputMethod: AuthFlowStep["input_method"]): string {
  switch (inputMethod) {
    case "FORM":
      return "Form input"
    case "CONSENT_SCREEN":
      return "OAuth consent screen"
    case "NONE":
      return "None (automatic)"
    default:
      return "Unknown"
  }
}

function getHttpStatusClass(status: number | null): string {
  if (status === null) return ""
  if (status >= 200 && status < 300) return "success"
  return "error"
}

// --- Sub-components for HTTP request details ---

interface HttpRequestDetailProps {
  request: AuthFlowHttpRequestDetail
}

function HttpRequestDetail({ request }: HttpRequestDetailProps) {
  const hasError = !!request.error || (request.response_status && request.response_status >= 400)

  return (
    <div className={`auth-test-http-request ${hasError ? "error" : ""}`}>
      <div className="auth-test-http-request-header">
        <span className="auth-test-http-request-method">{request.method}</span>
        {request.response_status && (
          <span className={`auth-test-http-request-status ${getHttpStatusClass(request.response_status)}`}>
            {request.response_status}
          </span>
        )}
      </div>
      <div className="auth-test-http-request-url">{request.url}</div>
      {request.duration_ms && (
        <div className="auth-test-http-request-duration">{request.duration_ms}ms</div>
      )}
      {request.error && (
        <div className="auth-test-step-error">
          <p>Error: {request.error}</p>
        </div>
      )}
      {request.request_headers && (
        <details className="auth-test-http-request-detail">
          <summary>Request headers</summary>
          <pre className="auth-test-code-block">
            {JSON.stringify(request.request_headers, null, 2)}
          </pre>
        </details>
      )}
      {request.request_body && (
        <details className="auth-test-http-request-detail">
          <summary>Request body</summary>
          <pre className="auth-test-code-block">
            {JSON.stringify(request.request_body, null, 2)}
          </pre>
        </details>
      )}
      {request.response_headers && (
        <details className="auth-test-http-request-detail">
          <summary>Response headers</summary>
          <pre className="auth-test-code-block">
            {JSON.stringify(request.response_headers, null, 2)}
          </pre>
        </details>
      )}
      {request.response_body && (
        <details className="auth-test-http-request-detail">
          <summary>Response body</summary>
          <pre className="auth-test-code-block">{request.response_body}</pre>
        </details>
      )}
    </div>
  )
}

// --- Status-specific action components ---
// Each component handles one specific state, making the logic explicit

interface StepActionProps {
  step: AuthFlowStep
  state: StepState
  stepIndex: number
  currentStepIndex: number | null
  isExecuting: boolean
  onRun: (stepName: string, stepIndex: number) => void
  onRetry: (stepName: string) => void
  onFormSubmit: (stepName: string, values: Record<string, string>) => void
}

function StepPendingAction({
  step,
  stepIndex,
  currentStepIndex,
  isExecuting,
  onRun,
}: StepActionProps) {
  const canRun = !isExecuting && (currentStepIndex === null || currentStepIndex === stepIndex)

  if (!canRun) return null

  return (
    <div className="auth-test-section-content">
      <button className="button-tertiary" onClick={() => onRun(step.name, stepIndex)}>
        Run this step
      </button>
    </div>
  )
}

function StepRunningAction() {
  // Running state has no actions - just shows status in header
  return null
}

function StepNeedsInputAction({
  step,
  state,
  isExecuting,
  onFormSubmit,
}: StepActionProps) {
  const formFields = (step.config?.fields as AuthFlowFormField[] | undefined) || []

  if (formFields.length === 0) return null

  return (
    <div className="auth-test-section-content-lg">
      {state.result?.error_message && (
        <div className="auth-test-error-message">{state.result.error_message}</div>
      )}
      <AuthFlowStepForm
        fields={formFields}
        description={(step.config?.description as string) || null}
        onSubmit={values => onFormSubmit(step.name, values)}
        isSubmitting={isExecuting}
      />
    </div>
  )
}

function StepNeedsRedirectAction({ state }: StepActionProps) {
  const redirectUrl = state.result?.redirect_url
  if (!redirectUrl) return null

  return (
    <div className="auth-test-section-content-lg">
      <p>Redirecting to OAuth provider for authorization...</p>
      <p className="helper-text">
        After authorizing, you will be redirected back to this page to complete the flow.
      </p>
    </div>
  )
}

function StepCompletedAction({
  step,
  state,
  isExecuting,
  onRetry,
}: StepActionProps) {
  return (
    <>
      {!isExecuting && (
        <div className="auth-test-section-content">
          <button className="button-tertiary" onClick={() => onRetry(step.name)}>
            Retry this step
          </button>
        </div>
      )}
      {state.contextChanges && (
        <div className="auth-test-section-content">
          <details className="auth-test-output-context">
            <summary>Context changes from this step</summary>
            <pre className="auth-test-code-block-success">
              {JSON.stringify(state.contextChanges, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </>
  )
}

function StepFailedAction({
  step,
  state,
  isExecuting,
  onRetry,
}: StepActionProps) {
  return (
    <>
      {!isExecuting && (
        <div className="auth-test-section-content">
          <button className="button-tertiary" onClick={() => onRetry(step.name)}>
            Retry this step
          </button>
        </div>
      )}
      {state.error && (
        <div className="auth-test-step-error">
          <p>Error: {state.error}</p>
        </div>
      )}
    </>
  )
}

// Map status to action component
const StatusActionComponents: Record<string, React.ComponentType<StepActionProps>> = {
  pending: StepPendingAction,
  running: StepRunningAction,
  needs_input: StepNeedsInputAction,
  needs_redirect: StepNeedsRedirectAction,
  completed: StepCompletedAction,
  failed: StepFailedAction,
}

// --- Step header component ---
// Uses existing CSS patterns from _api.scss for status dot (::before) and expand arrow (::after)

interface StepHeaderProps {
  step: AuthFlowStep
  status: StepStatus
}

function StepHeader({ step, status }: StepHeaderProps) {
  const statusLabels: Record<string, string> = {
    running: " (running...)",
    needs_input: " (waiting for input)",
    needs_redirect: " (waiting for redirect)",
  }

  return (
    <header>
      {formatStepType(step.step_type)}
      {statusLabels[status] || ""}
    </header>
  )
}

// --- Step info component ---

interface StepInfoProps {
  step: AuthFlowStep
}

function StepInfo({ step }: StepInfoProps) {
  return (
    <div className="auth-test-step-info">
      <dl className="auth-test-step-meta">
        <dt>Name:</dt>
        <dd><code>{step.name}</code></dd>
        <dt>Type:</dt>
        <dd><code>{step.step_type}</code></dd>
        <dt>Input method:</dt>
        <dd>{formatInputMethod(step.input_method)}</dd>
        <dt>Requires user input:</dt>
        <dd>{step.needs_user_input ? "Yes" : "No"}</dd>
      </dl>

      <details className="auth-test-step-config">
        <summary>Step config</summary>
        <pre className="auth-test-code-block">
          {JSON.stringify(step.config, null, 2)}
        </pre>
      </details>
    </div>
  )
}

// --- HTTP requests component ---

interface StepHttpRequestsProps {
  requests: AuthFlowHttpRequestDetail[] | null | undefined
}

function StepHttpRequests({ requests }: StepHttpRequestsProps) {
  if (!requests || requests.length === 0) return null

  return (
    <div className="auth-test-section-content">
      <details className="auth-test-http-requests">
        <summary>HTTP requests ({requests.length})</summary>
        {requests.map((req, reqIndex) => (
          <HttpRequestDetail key={reqIndex} request={req} />
        ))}
      </details>
    </div>
  )
}

// --- Main StepCard component ---

export interface StepCardProps {
  step: AuthFlowStep
  stepIndex: number
  state: StepState
  currentStepIndex: number | null
  isExecuting: boolean
  onRun: (stepName: string, stepIndex: number) => void
  onRetry: (stepName: string) => void
  onFormSubmit: (stepName: string, values: Record<string, string>) => void
}

export function StepCard({
  step,
  stepIndex,
  state,
  currentStepIndex,
  isExecuting,
  onRun,
  onRetry,
  onFormSubmit,
}: StepCardProps) {
  const [expanded, setExpanded] = useState(false)
  const statusClass = getStatusDisplayClass(state.status)
  const expandClass = expanded ? "expanded" : "collapsed"
  const ActionComponent = StatusActionComponents[state.status] || StepPendingAction

  // Auto-expand when step needs user input, collapse after submission
  useEffect(() => {
    if (state.status === "needs_input") {
      setExpanded(true)
    } else if (state.status === "running" || state.status === "completed") {
      setExpanded(false)
    }
  }, [state.status])

  const toggleExpanded = () => setExpanded(prev => !prev)

  return (
    <div
      className={`publish-item ${statusClass} ${expandClass}`}
      onClick={toggleExpanded}
    >
      <StepHeader step={step} status={state.status} />

      {expanded && (
        <div className="auth-test-step-body" onClick={e => e.stopPropagation()}>
          <StepInfo step={step} />

          <ActionComponent
            step={step}
            state={state}
            stepIndex={stepIndex}
            currentStepIndex={currentStepIndex}
            isExecuting={isExecuting}
            onRun={onRun}
            onRetry={onRetry}
            onFormSubmit={onFormSubmit}
          />

          <StepHttpRequests requests={state.result?.http_requests} />
        </div>
      )}
    </div>
  )
}

export default StepCard
